import { useEffect, useState, useCallback } from "react";
import { BellRing, Mail, RefreshCw, CheckCircle2, AlertTriangle, HelpCircle, XCircle, Clock, BellOff, Radio, CalendarRange, Globe2, Send } from "lucide-react";
import api from "@/lib/api";

const STATUS_BADGE = {
  pending: { label: "Pending", cls: "text-zinc-300 bg-white/[0.04] ring-white/10", Icon: Clock },
  sending: { label: "Sending", cls: "text-amber-200 bg-amber-300/10 ring-amber-300/30", Icon: RefreshCw },
  sent: { label: "Sent", cls: "text-emerald-300 bg-emerald-400/10 ring-emerald-400/30", Icon: CheckCircle2 },
  unknown: { label: "Unknown", cls: "text-amber-200 bg-amber-300/10 ring-amber-300/30", Icon: HelpCircle },
  failed: { label: "Failed", cls: "text-rose-300 bg-rose-400/10 ring-rose-400/30", Icon: XCircle },
  suppressed: { label: "Suppressed", cls: "text-zinc-400 bg-white/[0.03] ring-white/10", Icon: BellOff },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

// Watchtower Notifications — permanent, Super Admin/Owner-controlled delivery of critical-incident
// and verified-recovery emails (reuses the existing Watchtower incident engine; this panel only
// shows/toggles delivery). Two modes: Continuous (always on while enabled) or Scheduled (only
// within a Founder-edited date range). Recipient is fixed and never editable from here.
export function WatchtowerNotify() {
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [err, setErr] = useState("");
  const [saveErr, setSaveErr] = useState("");
  const [runResult, setRunResult] = useState("");
  const [testResult, setTestResult] = useState("");
  const [draftMode, setDraftMode] = useState("continuous");
  const [draftStart, setDraftStart] = useState("");
  const [draftEnd, setDraftEnd] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        api.get("/superadmin/watchtower/vacation-notify/config"),
        api.get("/superadmin/watchtower/vacation-notify/history", { params: { limit: 25 } }),
      ]);
      setCfg(c.data); setHistory(h.data.notifications || []); setErr("");
      setDraftMode(c.data.mode || "continuous");
      setDraftStart(c.data.schedule_start || "");
      setDraftEnd(c.data.schedule_end || "");
    } catch {
      setErr("Could not load Watchtower Notifications status.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scheduleInvalid = draftMode === "scheduled" && (!draftStart || !draftEnd || draftStart > draftEnd);

  const saveSchedule = async () => {
    if (!cfg || busy) return;
    if (draftMode === "scheduled" && scheduleInvalid) {
      setSaveErr(!draftStart || !draftEnd ? "Pick both a start and an end date for Scheduled mode." : "The start date must be on or before the end date.");
      return;
    }
    setBusy(true);
    setSaveErr("");
    try {
      const r = await api.post("/superadmin/watchtower/vacation-notify/config", {
        enabled: cfg.enabled, mode: draftMode,
        schedule_start: draftMode === "scheduled" ? draftStart : null,
        schedule_end: draftMode === "scheduled" ? draftEnd : null,
      });
      setCfg(r.data);
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || "Could not save the notification settings.");
    } finally {
      setBusy(false);
    }
  };

  const toggleMaster = async () => {
    if (!cfg || busy) return;
    const turningOn = !cfg.enabled;
    // The master switch only ever flips ON/OFF — it always uses the LAST SAVED mode/dates, never an
    // unsaved draft, so turning OFF is never blocked by (and never silently applies) an in-progress,
    // possibly-invalid schedule edit.
    if (turningOn && !window.confirm(`Turn ON critical incident emails to ${cfg.recipient}? A real email will be sent to this address the next time a critical incident opens or recovers${cfg.mode === "scheduled" ? " while inside the saved date range" : ""}.`)) return;
    setBusy(true);
    setSaveErr("");
    try {
      const r = await api.post("/superadmin/watchtower/vacation-notify/config", {
        enabled: turningOn, mode: cfg.mode, schedule_start: cfg.schedule_start, schedule_end: cfg.schedule_end,
      });
      setCfg(r.data);
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || "Could not update the notification toggle.");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setRunResult("");
    try {
      const r = await api.post("/superadmin/watchtower/vacation-notify/run-now");
      setRunResult(r.data.enabled === false ? "OFF — nothing was sent." : `Checked pending alerts — processed ${r.data.processed}, sent ${r.data.sent || 0}.`);
      await load();
    } catch {
      setErr("Could not run the notification check.");
    } finally {
      setRunning(false);
    }
  };

  const sendTestEmail = async () => {
    setTesting(true);
    setTestResult("");
    try {
      const r = await api.post("/superadmin/watchtower/vacation-notify/test-email");
      setTestResult(r.data.status === "sent" ? `Sample email sent to ${r.data.recipient} — check that inbox now.` :
        r.data.status === "unknown" ? `Sample email was submitted to ${r.data.recipient}, but delivery could not be confirmed.` :
          `Could not send the sample email to ${r.data.recipient}.`);
    } catch {
      setTestResult("Could not send the sample email.");
    } finally {
      setTesting(false);
    }
  };

  const settingsDirty = cfg && (draftMode !== (cfg.mode || "continuous") || draftStart !== (cfg.schedule_start || "") || draftEnd !== (cfg.schedule_end || ""));

  return (
    <section className="rounded-2xl ring-1 ring-white/[0.06] bg-zinc-900/40 p-5 sm:p-6 space-y-5" data-testid="watchtower-notify-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-rose-400/15 ring-1 ring-rose-400/30 flex items-center justify-center text-rose-300 shrink-0"><BellRing className="w-4.5 h-4.5" /></div>
          <div className="min-w-0">
            <p className="font-semibold tracking-tight" data-testid="watchtower-notify-title">Watchtower Notifications</p>
            <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5"><Mail className="w-3 h-3" /> Critical incident emails to <span className="text-zinc-300" data-testid="watchtower-notify-recipient">{cfg?.recipient || "info@neecdigital.com"}</span></p>
          </div>
        </div>
        {cfg && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={sendTestEmail} disabled={testing} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs ring-1 ring-white/10 hover:bg-white/5 disabled:opacity-50" data-testid="watchtower-notify-send-test">
              <Send className={`w-3.5 h-3.5 ${testing ? "animate-pulse" : ""}`} /> Send me a sample
            </button>
            <button onClick={runNow} disabled={running} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs ring-1 ring-white/10 hover:bg-white/5 disabled:opacity-50" data-testid="watchtower-notify-run-now">
              <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} /> Run check now
            </button>
            <span className="text-xs text-zinc-400" data-testid="watchtower-notify-toggle-label">{cfg.enabled ? "On" : "Off"}</span>
            <button onClick={toggleMaster} disabled={busy} data-testid="watchtower-notify-toggle" role="switch" aria-checked={cfg.enabled}
              aria-label={cfg.enabled ? "Turn off Watchtower notification emails" : "Turn on Watchtower notification emails"}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${cfg.enabled ? "bg-emerald-500/80" : "bg-white/10"}`}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${cfg.enabled ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        )}
      </div>

      {cfg && (
        <>
          <p className="text-xs text-zinc-500" data-testid="watchtower-notify-state-text">
            {!cfg.enabled ? "OFF — no emails will be sent. Watchtower keeps polling, detecting and logging incidents either way." :
              cfg.active_now ? "ON and currently active — a critical incident or a verified recovery will email the address above." :
                "ON but currently silent — the selected schedule window is not active right now, so nothing will be emailed until it is."}
          </p>

          <div className="rounded-xl ring-1 ring-white/[0.06] bg-black/20 p-4 space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">Delivery mode</span>
              <span className="text-[11px] text-zinc-600 flex items-center gap-1 ml-auto"><Globe2 className="w-3 h-3" /> {cfg.timezone || "America/Jamaica"}</span>
            </div>
            <div className="flex gap-2" role="radiogroup" aria-label="Delivery mode">
              <button type="button" onClick={() => setDraftMode("continuous")} data-testid="watchtower-notify-mode-continuous"
                aria-checked={draftMode === "continuous"} role="radio"
                className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm ring-1 transition-colors ${draftMode === "continuous" ? "bg-teal-400/15 ring-teal-400/40 text-teal-200" : "ring-white/10 text-zinc-400 hover:bg-white/5"}`}>
                <Radio className="w-3.5 h-3.5" /> Continuous
              </button>
              <button type="button" onClick={() => setDraftMode("scheduled")} data-testid="watchtower-notify-mode-scheduled"
                aria-checked={draftMode === "scheduled"} role="radio"
                className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm ring-1 transition-colors ${draftMode === "scheduled" ? "bg-teal-400/15 ring-teal-400/40 text-teal-200" : "ring-white/10 text-zinc-400 hover:bg-white/5"}`}>
                <CalendarRange className="w-3.5 h-3.5" /> Scheduled
              </button>
            </div>
            <p className="text-[11px] text-zinc-500">
              {draftMode === "continuous" ? "Qualifying alerts deliver any time notifications are ON — no dates needed." : "Qualifying alerts deliver only between the dates below (inclusive, America/Jamaica time)."}
            </p>

            {draftMode === "scheduled" && (
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-zinc-400 space-y-1">
                  <span>Start date</span>
                  <input type="date" value={draftStart} onChange={(e) => setDraftStart(e.target.value)} data-testid="watchtower-notify-schedule-start"
                    className="w-full rounded-lg bg-black/30 ring-1 ring-white/10 px-2.5 py-1.5 text-zinc-200 focus:ring-teal-400/50 outline-none" />
                </label>
                <label className="text-xs text-zinc-400 space-y-1">
                  <span>End date</span>
                  <input type="date" value={draftEnd} onChange={(e) => setDraftEnd(e.target.value)} data-testid="watchtower-notify-schedule-end"
                    className="w-full rounded-lg bg-black/30 ring-1 ring-white/10 px-2.5 py-1.5 text-zinc-200 focus:ring-teal-400/50 outline-none" />
                </label>
              </div>
            )}

            {saveErr && <p className="text-xs text-rose-300" data-testid="watchtower-notify-save-error">{saveErr}</p>}

            <button onClick={saveSchedule} disabled={busy || !settingsDirty} data-testid="watchtower-notify-save-settings"
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium ring-1 ring-teal-400/30 bg-teal-400/10 text-teal-200 hover:bg-teal-400/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              {busy ? "Saving…" : "Save schedule settings"}
            </button>
          </div>
        </>
      )}

      {runResult && <p className="text-xs text-teal-300" data-testid="watchtower-notify-run-result">{runResult}</p>}
      {testResult && <p className="text-xs text-teal-300" data-testid="watchtower-notify-test-result">{testResult}</p>}
      {err && <p className="text-xs text-rose-300" data-testid="watchtower-notify-error">{err}</p>}

      <div>
        <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-2">Recent notification history</p>
        {history.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="watchtower-notify-history-empty">No notifications yet — Watchtower has not seen a critical incident since this was built.</p>
        ) : (
          <ul className="space-y-1.5" data-testid="watchtower-notify-history-list">
            {history.map((n) => {
              const badge = STATUS_BADGE[n.status] || STATUS_BADGE.pending;
              const Icon = badge.Icon;
              return (
                <li key={n.id} className="flex items-center gap-3 rounded-xl ring-1 ring-white/[0.05] bg-black/20 px-3 py-2 text-sm" data-testid="watchtower-notify-history-row">
                  {n.transition === "opened" ? <AlertTriangle className="w-4 h-4 text-rose-300 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-zinc-200 truncate">{n.title || n.what_failed} · {n.transition === "opened" ? "Opened" : "Resolved"}</p>
                    <p className="text-[11px] text-zinc-500">{fmt(n.created_at)}{n.duplicate_risk ? " · duplicate-send risk flagged" : ""}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 shrink-0 ${badge.cls}`} data-testid={`watchtower-notify-status-${n.status}`}>
                    <Icon className="w-3 h-3" /> {badge.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
